import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient from '../api/client'
import { useAuth } from '../context/AuthContext'

interface UserRecord {
  email: string
  name?: string
  mobile?: string
  role?: string
  plan?: string
  plan_start_date?: string
  plan_expires_at?: string
  created_at?: string
}

export function AdminPage() {
  const { isSuperAdmin } = useAuth()
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (!isSuperAdmin) { navigate('/dashboard', { replace: true }); return }
    fetchUsers()
  }, [isSuperAdmin])

  async function fetchUsers() {
    setLoading(true)
    try {
      const res = await apiClient.get('/admin/users')
      setUsers(res.data)
    } catch {
      setMessage({ type: 'error', text: 'Failed to load users.' })
    } finally {
      setLoading(false)
    }
  }

  async function changePlan(email: string, plan: 'free' | 'pro') {
    setUpdating(email)
    setMessage(null)
    try {
      const res = await apiClient.put(`/admin/users/${encodeURIComponent(email)}/plan`, { plan })
      setUsers(prev => prev.map(u =>
        u.email === email
          ? { ...u, plan: res.data.plan, plan_start_date: res.data.plan_start_date, plan_expires_at: res.data.plan_expires_at }
          : u
      ))
      setMessage({ type: 'success', text: `${email} updated to ${plan} plan.` })
    } catch {
      setMessage({ type: 'error', text: `Failed to update plan for ${email}.` })
    } finally {
      setUpdating(null)
    }
  }

  function formatDate(iso?: string) {
    if (!iso) return '—'
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  function isPlanExpired(iso?: string) {
    if (!iso) return false
    return new Date(iso) < new Date()
  }

  if (!isSuperAdmin) return null

  return (
    <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>Admin Panel</h1>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>Manage user license plans (free / pro · 30 days)</p>

      {message && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem',
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b',
          border: `1px solid ${message.type === 'success' ? '#6ee7b7' : '#fca5a5'}`,
        }}>
          {message.text}
        </div>
      )}

      {loading ? (
        <p style={{ color: '#6b7280' }}>Loading users...</p>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e5e7eb' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['Email', 'Name', 'Plan', 'Started', 'Expires', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontWeight: 600, color: '#374151' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const expired = isPlanExpired(u.plan_expires_at)
                const effectivePlan = expired ? 'expired' : (u.plan ?? 'free')
                return (
                  <tr key={u.email} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>{u.email}</td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{u.name ?? '—'}</td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <span style={{
                        display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '999px',
                        fontSize: '0.75rem', fontWeight: 600,
                        background: effectivePlan === 'pro' ? '#dbeafe' : effectivePlan === 'expired' ? '#fee2e2' : '#f3f4f6',
                        color: effectivePlan === 'pro' ? '#1d4ed8' : effectivePlan === 'expired' ? '#dc2626' : '#374151',
                      }}>
                        {effectivePlan}
                      </span>
                    </td>
                    <td style={{ padding: '0.75rem 1rem', color: '#6b7280' }}>{formatDate(u.plan_start_date)}</td>
                    <td style={{ padding: '0.75rem 1rem', color: expired ? '#dc2626' : '#6b7280' }}>
                      {formatDate(u.plan_expires_at)}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => changePlan(u.email, 'pro')}
                          disabled={updating === u.email || (effectivePlan === 'pro')}
                          style={{
                            padding: '0.3rem 0.8rem', borderRadius: '6px', border: 'none',
                            background: effectivePlan === 'pro' ? '#e5e7eb' : '#1d4ed8',
                            color: effectivePlan === 'pro' ? '#9ca3af' : '#fff',
                            cursor: effectivePlan === 'pro' ? 'default' : 'pointer',
                            fontWeight: 600, fontSize: '0.8rem',
                          }}
                        >
                          {updating === u.email ? '...' : 'Set Pro'}
                        </button>
                        <button
                          onClick={() => changePlan(u.email, 'free')}
                          disabled={updating === u.email || effectivePlan === 'free'}
                          style={{
                            padding: '0.3rem 0.8rem', borderRadius: '6px', border: '1px solid #d1d5db',
                            background: '#fff', color: effectivePlan === 'free' ? '#9ca3af' : '#374151',
                            cursor: effectivePlan === 'free' ? 'default' : 'pointer',
                            fontWeight: 600, fontSize: '0.8rem',
                          }}
                        >
                          {updating === u.email ? '...' : 'Set Free'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
